import React, { useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, ShoppingCart, Sparkles, Check, X, ShieldCheck } from "lucide-react";
import { useCart } from "@/context/CartContext";

const FORMAT_BADGES = {
  curso: "bg-brand-orange-500/10 text-brand-orange-700 border-primary/30",
  diplomado: "bg-purple-500/10 text-purple-800 border-purple-500/30",
  programa: "bg-emerald-500/10 text-emerald-800 border-emerald-500/30",
  seminario: "bg-amber-500/10 text-amber-800 border-amber-500/30",
  certificacion: "bg-rose-500/10 text-rose-800 border-rose-500/30",
};

const IADA_PCT = 0.30;

function fmtMxn(n) {
  if (!n && n !== 0) return null;
  return "$" + Number(n).toLocaleString("es-MX") + " MXN";
}

function fmtDate(iso) {
  if (!iso) return null;
  try {
    const d = new Date(iso);
    return d.toLocaleDateString("es-MX", { day: "numeric", month: "short" });
  } catch { return iso; }
}

export default function ProgramCard({ program, hasAllAccess = false }) {
  const { cart, addItem } = useCart();
  const inCart = cart?.items?.some((i) => i.program_id === program.id);
  const canBuy = !!program.price_mxn && !!program.next_cohort_start;
  const badgeCls = FORMAT_BADGES[program.format] || "bg-gray-500/10 text-[#0B0B0B] border-gray-500/30";
  const price = fmtMxn(program.price_mxn);
  const priceWithAval = fmtMxn(Math.round((program.price_mxn || 0) * (1 + IADA_PCT)));
  const nextCohort = fmtDate(program.next_cohort_start);
  const iadaEligible = !!program.iada_aval_available;
  const [modalOpen, setModalOpen] = useState(false);

  const handleAdd = () => {
    if (!canBuy || inCart) return;
    if (iadaEligible) {
      setModalOpen(true);
    } else {
      addItem(program.id, false);
    }
  };

  const confirmAdd = (withAval) => {
    addItem(program.id, withAval);
    setModalOpen(false);
  };

  return (
    <>
    <article
      data-testid={`program-card-${program.code}`}
      className="group flex flex-col p-5 bg-white border border-gray-200 rounded-2xl shadow-sm hover:shadow-lg hover:border-[#F26A21] transition-all duration-300 hover:-translate-y-1"
    >
      <div className="flex items-start justify-between mb-3">
        <span className="text-[10px] font-semibold uppercase tracking-widest text-gray-600">{program.area_name}</span>
        <span className="font-mono text-[11px] text-gray-500">{program.code}</span>
      </div>

      <Link
        to={`/catalogo/${program.slug}`}
        data-testid={`program-card-title-${program.code}`}
        className="block"
      >
        <h3 className="text-lg font-semibold text-[#0B0B0B] leading-snug group-hover:text-[#F26A21] transition-colors">
          {program.title}
        </h3>
      </Link>

      {hasAllAccess && (
        <span
          className="mt-2 inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-primary/10 border border-primary/40 text-[#B84A0C] text-[10px] font-bold uppercase tracking-widest"
          data-testid={`program-all-access-pill-${program.code}`}
        >
          <ShieldCheck className="w-3 h-3" /> Incluido en tu All-Access
        </span>
      )}

      {program.rating_count > 0 && (
        <div className="mt-2 inline-flex items-center gap-1 text-xs"
             data-testid={`program-rating-chip-${program.code}`}>
          <span className="text-[#F26A21]">★</span>
          <span className="font-semibold text-[#0B0B0B]">{Number(program.rating_avg || 0).toFixed(1)}</span>
          <span className="text-gray-500">· {program.rating_count} opini{program.rating_count === 1 ? "ón" : "ones"}</span>
        </div>
      )}

      <div className="mt-4 flex flex-wrap gap-2">
        <span className={`text-[11px] px-2 py-0.5 rounded border ${badgeCls} uppercase font-semibold tracking-wider`}>
          {program.format}
        </span>
        <span className="text-[11px] px-2 py-0.5 rounded border border-gray-300 text-[#0B0B0B] uppercase font-semibold tracking-wider">
          {program.modality}
        </span>
        {iadaEligible && (
          <span className="text-[11px] px-2 py-0.5 rounded bg-[#F26A21] text-[#0B0B0B] uppercase font-semibold tracking-wider inline-flex items-center gap-1"
            data-testid={`program-iada-${program.code}`}>
            <Sparkles className="w-3 h-3" aria-hidden="true" /> IADA · Anáhuac
          </span>
        )}
      </div>

      {price !== null && (
        <div className="mt-4" data-testid={`program-price-${program.code}`}>
          <p className="text-[10px] uppercase tracking-widest text-gray-600 font-semibold">Desde</p>
          <div className="mt-1 flex items-baseline gap-2 flex-wrap">
            <p className="text-3xl font-extrabold text-primary leading-none tracking-tight tabular-nums" style={{ letterSpacing: "-0.02em" }}>
              {price}
            </p>
            <span className="text-xs font-semibold text-gray-700 uppercase tracking-widest">MXN + IVA</span>
          </div>
          <p className="mt-1.5 text-[11px] text-gray-600">
            {nextCohort ? (
              <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded-full bg-emerald-500/10 text-emerald-800 border border-emerald-500/30 text-[10px] font-semibold uppercase tracking-wider"
                    data-testid={`program-cohort-chip-${program.code}`}>
                Cohorte {nextCohort}
              </span>
            ) : (
              <span className="text-gray-500">Consultar fechas</span>
            )}
          </p>
          {iadaEligible && (
            <p className="mt-1 text-[11px] text-[#F26A21] font-semibold" data-testid={`program-iada-note-${program.code}`}>
              + Aval IADA disponible (+30%)
            </p>
          )}
          {!hasAllAccess && (
            <Link
              to="/planes"
              data-testid={`program-upsell-all-access-${program.code}`}
              className="mt-1 inline-flex items-center gap-1 text-[10px] text-[#B84A0C] hover:text-primary font-semibold underline underline-offset-2"
            >
              o incluido con All-Access desde $2,500/mes →
            </Link>
          )}
        </div>
      )}

      <div className="mt-auto pt-4 flex items-center gap-2 border-t border-gray-200/70 mt-4">
        <Link
          to={`/catalogo/${program.slug}`}
          data-testid={`program-detail-btn-${program.code}`}
          className="flex-1 inline-flex items-center justify-center gap-1 text-xs px-3 py-1.5 rounded-md border border-[#F26A21] text-[#F26A21] hover:bg-[#F26A21]/10 font-semibold"
        >
          Ver detalle <ArrowRight className="w-3 h-3" />
        </Link>
        {canBuy ? (
          <button
            type="button"
            onClick={handleAdd}
            disabled={inCart}
            data-testid={`program-cart-btn-${program.code}`}
            aria-label={inCart ? "Ya en carrito" : "Agregar al carrito"}
            className={`flex-1 inline-flex items-center justify-center gap-1 text-xs px-3 py-1.5 rounded-md font-semibold ${
              inCart
                ? "bg-emerald-500/20 text-emerald-800 border border-emerald-500/40 cursor-default"
                : "bg-[#F26A21] hover:bg-[#D9541A] text-[#0B0B0B]"
            }`}
          >
            {inCart ? (<><Check className="w-3 h-3" /> En carrito</>) : (<><ShoppingCart className="w-3 h-3" /> Agregar</>)}
          </button>
        ) : (
          <Link
            to="/contacto"
            data-testid={`program-contact-btn-${program.code}`}
            aria-label="Contactar para próximas fechas"
            title="Sin cohortes públicas — contáctanos"
            className="flex-1 inline-flex items-center justify-center gap-1 text-xs px-3 py-1.5 rounded-md bg-gray-200 text-[#0B0B0B] hover:bg-gray-300 font-semibold"
          >
            Contactar
          </Link>
        )}
      </div>
    </article>

    {modalOpen && (
      <IadaChoiceModal
        program={program}
        priceNoAval={price}
        priceWithAval={priceWithAval}
        onClose={() => setModalOpen(false)}
        onConfirm={confirmAdd}
      />
    )}
    </>
  );
}

function IadaChoiceModal({ program, priceNoAval, priceWithAval, onClose, onConfirm }) {
  return (
    <div className="fixed inset-0 z-50 bg-black/70 flex items-center justify-center p-4"
         onClick={onClose} data-testid="iada-choice-modal">
      <div className="w-full max-w-md bg-white rounded-xl border border-gray-200 p-6"
           onClick={(e) => e.stopPropagation()}>
        <div className="flex items-start justify-between mb-4">
          <div>
            <p className="text-[10px] uppercase tracking-widest text-[#F26A21] font-semibold">Aval académico</p>
            <h3 className="mt-1 text-lg font-bold text-[#0B0B0B]">¿Quieres agregar aval IADA?</h3>
          </div>
          <button onClick={onClose} data-testid="iada-modal-close" aria-label="Cerrar" className="text-gray-400 hover:text-[#0B0B0B]">
            <X className="w-5 h-5" />
          </button>
        </div>
        <p className="text-sm text-gray-600 mb-4">
          <span className="font-semibold text-[#0B0B0B]">{program.title}</span> es elegible para aval
          académico de IADA — Instituto de Alta Dirección · Anáhuac Business School.
        </p>
        <div className="space-y-2">
          <button
            type="button"
            onClick={() => onConfirm(false)}
            data-testid="iada-modal-choose-no"
            className="w-full text-left p-3 rounded-lg border border-gray-300 hover:border-[#F26A21] transition-colors"
          >
            <p className="text-sm font-semibold text-[#0B0B0B]">Sin aval</p>
            <p className="text-xs text-gray-500 mt-0.5">Certificado DCC estándar</p>
            <p className="mt-2 font-mono text-base font-bold text-[#0B0B0B]">{priceNoAval}</p>
          </button>
          <button
            type="button"
            onClick={() => onConfirm(true)}
            data-testid="iada-modal-choose-yes"
            className="w-full text-left p-3 rounded-lg border-2 border-[#F26A21] bg-[#F26A21]/5 hover:bg-[#F26A21]/10 transition-colors"
          >
            <p className="text-sm font-semibold text-[#0B0B0B] flex items-center gap-1">
              <Sparkles className="w-3 h-3 text-[#F26A21]" />
              Con aval IADA <span className="text-[10px] text-[#F26A21] font-mono">+30%</span>
            </p>
            <p className="text-xs text-gray-500 mt-0.5">Certificado avalado por Universidad Anáhuac</p>
            <p className="mt-2 font-mono text-base font-bold text-[#F26A21]">{priceWithAval}</p>
          </button>
        </div>
        <p className="mt-4 text-[10px] text-gray-500 text-center">
          Puedes cambiar tu elección desde el carrito antes de pagar.
        </p>
      </div>
    </div>
  );
}
